import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ImageUploader } from '../components/ImageUploader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Languages } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { RichTextEditor } from '../components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettingsPage = () => {
  const { getAuthHeader } = useAuth();
  const [settings, setSettings] = useState({
    company_name: '',
    about_title_nl: '', about_title_en: '', about_title_fr: '', about_title_de: '',
    about_text_nl: '', about_text_en: '', about_text_fr: '', about_text_de: '',
    about_image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleChange = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers: getAuthHeader() });
      toast.success('Instellingen opgeslagen');
    } catch { toast.error('Fout bij opslaan'); }
    finally { setSaving(false); }
  };

  const translateAbout = async () => {
    if (!settings.about_title_nl && !settings.about_text_nl) {
      toast.error('Vul eerst de Nederlandse tekst in');
      return;
    }
    setTranslating(true);
    try {
      const updates = {};

      if (settings.about_title_nl) {
        const titleRes = await axios.post(`${API}/admin/translate`, {
          text: settings.about_title_nl, source_lang: 'nl', target_langs: ['en', 'fr', 'de']
        }, { headers: getAuthHeader() });
        const t = titleRes.data.translations;
        updates.about_title_en = t.en || '';
        updates.about_title_fr = t.fr || '';
        updates.about_title_de = t.de || '';
      }

      if (settings.about_text_nl) {
        const textRes = await axios.post(`${API}/admin/translate`, {
          text: settings.about_text_nl, source_lang: 'nl', target_langs: ['en', 'fr', 'de']
        }, { headers: getAuthHeader() });
        const t = textRes.data.translations;
        updates.about_text_en = t.en || '';
        updates.about_text_fr = t.fr || '';
        updates.about_text_de = t.de || '';
      }

      setSettings(prev => ({ ...prev, ...updates }));
      toast.success('Vertalingen succesvol gegenereerd!');
    } catch (err) {
      console.error(err);
      toast.error('Vertaling mislukt');
    } finally { setTranslating(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Instellingen</h1>
        <p className="text-gray-500 text-sm mt-1">Beheer uw site instellingen</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Name */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Bedrijfsgegevens</h2>
          <div className="max-w-md">
            <Label className="text-gray-700">Bedrijfsnaam</Label>
            <Input value={settings.company_name || ''} onChange={(e) => handleChange('company_name', e.target.value)} placeholder="Alphaprofiles" className="mt-1.5" />
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">Over Ons Pagina</h2>
            <Button type="button" variant="outline" size="sm" onClick={translateAbout} disabled={translating} className="text-xs">
              <Languages className="h-3.5 w-3.5 mr-1.5" />
              {translating ? 'Vertalen...' : 'AI Vertalen (NL → EN/FR/DE)'}
            </Button>
          </div>
          <p className="text-gray-500 text-sm mb-6">Vul de Nederlandse tekst in en klik op "AI Vertalen" voor automatische vertalingen</p>

          <div className="mb-6">
            <ImageUploader value={settings.about_image_url} onChange={(url) => handleChange('about_image_url', url)} label="Over Ons Afbeelding" />
          </div>

          <Tabs defaultValue="nl" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="nl">Nederlands</TabsTrigger>
              <TabsTrigger value="en">English</TabsTrigger>
              <TabsTrigger value="fr">Français</TabsTrigger>
              <TabsTrigger value="de">Deutsch</TabsTrigger>
            </TabsList>

            {[
              { lang: 'nl', titleLabel: 'Titel', titlePh: 'Over Alphaprofiles', textLabel: 'Tekst', textPh: 'Beschrijving over uw bedrijf...' },
              { lang: 'en', titleLabel: 'Title', titlePh: 'About Alphaprofiles', textLabel: 'Text', textPh: 'Description about your company...' },
              { lang: 'fr', titleLabel: 'Titre', titlePh: 'À Propos d\'Alphaprofiles', textLabel: 'Texte', textPh: 'Description de votre entreprise...' },
              { lang: 'de', titleLabel: 'Titel', titlePh: 'Über Alphaprofiles', textLabel: 'Text', textPh: 'Beschreibung Ihres Unternehmens...' },
            ].map(({ lang, titleLabel, titlePh, textLabel, textPh }) => (
              <TabsContent key={lang} value={lang} className="space-y-4">
                <div>
                  <Label className="text-gray-700">{titleLabel}</Label>
                  <Input value={settings[`about_title_${lang}`] || ''} onChange={(e) => handleChange(`about_title_${lang}`, e.target.value)} placeholder={titlePh} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-gray-700">{textLabel}</Label>
                  <div className="mt-1.5">
                    <RichTextEditor
                      value={settings[`about_text_${lang}`] || ''}
                      onChange={(v) => handleChange(`about_text_${lang}`, v)}
                      placeholder={textPh}
                    />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="bg-[#E31E24] hover:bg-[#c91a1f] text-white px-8">
            {saving ? 'Opslaan...' : 'Instellingen Opslaan'}
          </Button>
        </div>
      </form>
    </div>
  );
};
